/**
 * Data Catalog Service — wraps /api/admin/ai/data-catalog/
 * Phase 4.8-B — WQ-198
 */

// === Types ===

export interface CatalogEntry {
  id: string;
  name: string;
  description: string | null;
  source_type: string;
  connection_info: Record<string, unknown> | null;
  schema_info: Record<string, unknown> | null;
  quality_score: number | null;
  scan_status: string | null;
  is_active: boolean;
  automl_eligible: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface CatalogStats {
  total: number;
  active: number;
  by_source_type: Record<string, number>;
  avg_quality_score: number | null;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function listCatalogEntries(
  authFetch: FetchFn,
  filters?: { source_type?: string; is_active?: boolean; search?: string }
): Promise<CatalogEntry[]> {
  const params = new URLSearchParams();
  if (filters?.source_type) params.set('source_type', filters.source_type);
  if (filters?.is_active !== undefined) params.set('is_active', String(filters.is_active));
  if (filters?.search) params.set('search', filters.search);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/data-catalog/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list catalog entries: ${res.status}`);
  return res.json();
}

export async function searchCatalog(
  authFetch: FetchFn,
  query: string
): Promise<CatalogEntry[]> {
  const res = await authFetch(`/api/admin/ai/data-catalog/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error(`Failed to search catalog: ${res.status}`);
  return res.json();
}

export async function getCatalogStats(authFetch: FetchFn): Promise<CatalogStats> {
  const res = await authFetch('/api/admin/ai/data-catalog/stats');
  if (!res.ok) throw new Error(`Failed to get catalog stats: ${res.status}`);
  return res.json();
}

export async function getCatalogEntry(authFetch: FetchFn, id: string): Promise<CatalogEntry> {
  const res = await authFetch(`/api/admin/ai/data-catalog/${id}`);
  if (!res.ok) throw new Error(`Failed to get catalog entry: ${res.status}`);
  return res.json();
}

export async function createCatalogEntry(
  authFetch: FetchFn,
  data: Partial<CatalogEntry>
): Promise<CatalogEntry> {
  const res = await authFetch('/api/admin/ai/data-catalog/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create catalog entry: ${res.status}`);
  return res.json();
}

export async function updateCatalogEntry(
  authFetch: FetchFn,
  id: string,
  data: Partial<CatalogEntry>
): Promise<CatalogEntry> {
  const res = await authFetch(`/api/admin/ai/data-catalog/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update catalog entry: ${res.status}`);
  return res.json();
}

export async function deactivateCatalogEntry(authFetch: FetchFn, id: string): Promise<CatalogEntry> {
  const res = await authFetch(`/api/admin/ai/data-catalog/${id}/deactivate`, { method: 'PATCH' });
  if (!res.ok) throw new Error(`Failed to deactivate entry: ${res.status}`);
  return res.json();
}

export async function activateCatalogEntry(authFetch: FetchFn, id: string): Promise<CatalogEntry> {
  const res = await authFetch(`/api/admin/ai/data-catalog/${id}/activate`, { method: 'PATCH' });
  if (!res.ok) throw new Error(`Failed to activate entry: ${res.status}`);
  return res.json();
}

export async function getEntrySchema(authFetch: FetchFn, id: string): Promise<Record<string, unknown>> {
  const res = await authFetch(`/api/admin/ai/data-catalog/${id}/schema`);
  if (!res.ok) throw new Error(`Failed to get entry schema: ${res.status}`);
  return res.json();
}

export async function triggerScan(
  authFetch: FetchFn,
  body: { entry_id?: string; source_type?: string }
): Promise<Record<string, unknown>> {
  const res = await authFetch('/api/admin/ai/data-catalog/scan', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to trigger scan: ${res.status}`);
  return res.json();
}
